import {
  ChannelState,
  NotificationRowDefinition,
} from '../../notifications/src/notifications.model';

// Re-exported: the catalog/channel shapes are identical to SpanVA's (both resources are built on
// the same NotificationSettingsResourceMixin), so only the entity-id field differs.
export type { ChannelState, NotificationRowDefinition };

// ─── Catalog API (GET /notification_settings/catalog/) ────────────────────────

export interface AuditNotificationsCatalogResponse {
  notification_rows: NotificationRowDefinition[];
}

// ─── Summary API (GET /notification_settings/summary/) ────────────────────────
// Audit settings are tenant-wide, so `instances` always has exactly one entry —
// the tenant's own row, keyed by `tenant_id` (see AuditNotificationSettingsResource.get_entity_id_key).

export interface AuditNotificationInstance {
  tenant_id: string;
  tenant_name?: string;
  master_enabled: boolean;
  recipients: string[];
  channels: Record<string, ChannelState>;
}

export interface AuditNotificationsSummaryResponse {
  instances: AuditNotificationInstance[];
  notification_rows?: NotificationRowDefinition[];
}

// ─── PATCH payload (PATCH /notification_settings/{tenant_id}/) ─────────────────

export interface AuditNotificationPatchChannelPayload {
  enabled: boolean;
  frequency?: string;
}

export interface AuditNotificationPatchPayload {
  enable_all?: boolean;
  recipients?: string[];
  channels?: Record<string, AuditNotificationPatchChannelPayload>;
}

/** The PATCH endpoint may return a 200 with an `error` field instead of a real HTTP error. */
export interface AuditNotificationPatchResponse extends Partial<AuditNotificationInstance> {
  error?: string;
}

// ─── UI-layer derived types ────────────────────────────────────────────────────

/**
 * Single row shown in the Audit notifications table — mirrors SpanVA's NotificationTableRow
 * shape so the same p-table markup/behavior (sort, column selector, row highlight) is reused
 * as-is. There is always exactly one row: the tenant's own settings document.
 */
export type AuditNotificationTableRow = {
  tenant_id: string;
  name: string;
  master_enabled: boolean;
  recipients: string[];
  channelsByUiId: Record<string, ChannelState | undefined>;
};

/** Single source of truth for the row/drawer display name: "<tenant name> Audit Notifications". */
export function auditNotificationDisplayName(tenantName: string | undefined): string {
  return `${tenantName ?? ''} Audit Notifications`.trim();
}
